import { ConnectionLost } from "@/components/common/ConnectionLost";
import { LessonDescription } from "@/components/course/LessonDescription";
import { ModuleItem } from "@/components/course/ModuleItem";
import { ProgressRing } from "@/components/course/ProgressRing";
import { VideoPlayer } from "@/components/course/VideoPlayer";
import { QuizCard } from "@/components/quiz/QuizCard";
import { QuizCelebration } from "@/components/quiz/QuizCelebration";
import { QuizResult } from "@/components/quiz/QuizResult";
import { Button } from "@/components/ui/button";
import { useCourseContent } from "@/hooks/useCourseContent";
import { useQuiz } from "@/hooks/useQuiz";
import type { ModuleWithProgress, QuizResult as QuizResultType } from "@/lib/api-types";
import { NetworkError } from "@/lib/api-types";
import { queryKeys } from "@/lib/query-keys";
import { cn } from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ChevronDown, Loader2, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

export function CoursePlayer() {
	const { t } = useTranslation();
	const { courseSlug, moduleSlug } = useParams<{
		courseSlug: string;
		moduleSlug?: string;
	}>();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const [sidebarOpen, setSidebarOpen] = useState(true);
	const [quizAnswers, setQuizAnswers] = useState<Record<string, string[]>>({});
	const [quizResult, setQuizResult] = useState<QuizResultType | null>(null);
	const [showCelebration, setShowCelebration] = useState(false);

	const courseId = courseSlug ?? "";
	const { data: content, isLoading, isError, error } = useCourseContent(courseId);

	// Post-checkout polling
	const [pollCount, setPollCount] = useState(0);
	const isPolling = !content && !isError && pollCount < 15;

	useEffect(() => {
		if (content || isError || pollCount >= 15) return;
		const timeout = setTimeout(() => {
			queryClient.invalidateQueries({
				queryKey: queryKeys.courses.content(courseId),
			});
			setPollCount((c) => c + 1);
		}, 2000);
		return () => clearTimeout(timeout);
	}, [content, isError, pollCount, courseId, queryClient]);

	const sections = content?.sections ?? [];
	const modules = useMemo(() => sections.flatMap((s) => s.modules), [sections]);

	const currentModule = useMemo(() => {
		if (modules.length === 0) return null;
		if (moduleSlug) {
			return modules.find((m) => m.id === moduleSlug) ?? modules[0];
		}
		return modules.find((m) => m.progress?.status !== "completed") ?? modules[0];
	}, [modules, moduleSlug]);

	// Find which section the current module belongs to
	const currentSectionId = useMemo(() => {
		if (!currentModule) return null;
		return sections.find((s) => s.modules.some((m) => m.id === currentModule.id))?.id ?? null;
	}, [currentModule, sections]);

	// Collapsible sidebar sections: current section expanded, others collapsed
	const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());

	// When currentSectionId changes, expand it and collapse others
	useEffect(() => {
		if (!currentSectionId || sections.length === 0) return;
		const collapsed = new Set<string>();
		for (const s of sections) {
			if (s.id !== currentSectionId) {
				collapsed.add(s.id);
			}
		}
		setCollapsedSections(collapsed);
	}, [currentSectionId, sections]);

	function toggleSidebarSection(sectionId: string) {
		setCollapsedSections((prev) => {
			const next = new Set(prev);
			if (next.has(sectionId)) {
				next.delete(sectionId);
			} else {
				next.add(sectionId);
			}
			return next;
		});
	}

	const { quiz, submit } = useQuiz(currentModule?.has_quiz ? currentModule.id : "");

	const overallProgress = useMemo(() => {
		if (modules.length === 0) return 0;
		const completed = modules.filter((m) => m.progress?.status === "completed").length;
		return Math.round((completed / modules.length) * 100);
	}, [modules]);

	const navigateToModule = useCallback(
		(mod: ModuleWithProgress) => {
			setQuizResult(null);
			setQuizAnswers({});
			navigate(`/learn/${courseId}/${mod.id}`, { replace: true });
		},
		[courseId, navigate],
	);

	const goToNextModule = useCallback(() => {
		if (!currentModule) return;
		const idx = modules.findIndex((m) => m.id === currentModule.id);
		const next = modules[idx + 1];
		if (next) navigateToModule(next);
	}, [currentModule, modules, navigateToModule]);

	const goToPrevModule = useCallback(() => {
		if (!currentModule) return;
		const idx = modules.findIndex((m) => m.id === currentModule.id);
		const prev = modules[idx - 1];
		if (prev) navigateToModule(prev);
	}, [currentModule, modules, navigateToModule]);

	useEffect(() => {
		const handler = (e: KeyboardEvent) => {
			if (e.defaultPrevented || document.querySelector('[role="dialog"]')) return;
			if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
			switch (e.key) {
				case "t":
					setSidebarOpen((o) => !o);
					break;
				case "n":
					goToNextModule();
					break;
				case "p":
					goToPrevModule();
					break;
			}
		};
		window.addEventListener("keydown", handler);
		return () => window.removeEventListener("keydown", handler);
	}, [goToNextModule, goToPrevModule]);

	const handleVideoComplete = useCallback(() => {
		queryClient.invalidateQueries({
			queryKey: queryKeys.courses.content(courseId),
		});
		queryClient.invalidateQueries({ queryKey: queryKeys.enrollments });
	}, [courseId, queryClient]);

	const handleQuizSelect = useCallback(
		(questionId: string, optionId: string) => {
			setQuizAnswers((prev) => {
				const question = quiz?.questions.find((q) => q.id === questionId);
				if (!question) return prev;
				if (question.type === "single_choice") {
					return { ...prev, [questionId]: [optionId] };
				}
				const current = prev[questionId] ?? [];
				const next = current.includes(optionId)
					? current.filter((id) => id !== optionId)
					: [...current, optionId];
				return { ...prev, [questionId]: next };
			});
		},
		[quiz],
	);

	const handleQuizSubmit = useCallback(async () => {
		if (!quiz || !currentModule) return;
		const answers = quiz.questions.map((q) => ({
			question_id: q.id,
			answer:
				q.type === "single_choice" ? (quizAnswers[q.id]?.[0] ?? "") : (quizAnswers[q.id] ?? []),
		}));
		const result = await submit.mutateAsync({ answers });
		setQuizResult(result);
		if (result.passed) {
			setShowCelebration(true);
			setTimeout(() => setShowCelebration(false), 3000);
			queryClient.invalidateQueries({
				queryKey: queryKeys.courses.content(courseId),
			});
		}
	}, [quiz, currentModule, quizAnswers, submit, courseId, queryClient]);

	const handleQuizRetry = useCallback(() => {
		setQuizResult(null);
		setQuizAnswers({});
	}, []);

	// Loading / polling state
	if (isLoading || isPolling) {
		return (
			<div className="flex h-[calc(100dvh-48px)] items-center justify-center" aria-busy="true">
				<div className="text-center">
					<Loader2 className="mx-auto mb-4 h-8 w-8 animate-spin text-accent" aria-hidden="true" />
					<p className="text-secondary">
						{isPolling ? t("course.settingUp") : t("course.loading")}
					</p>
				</div>
			</div>
		);
	}

	if (isError && error instanceof NetworkError) {
		return (
			<div className="flex h-[calc(100dvh-48px)] items-center justify-center">
				<ConnectionLost />
			</div>
		);
	}

	if (isError) {
		return (
			<div className="flex h-[calc(100dvh-48px)] items-center justify-center">
				<div className="flex flex-col items-center gap-4 text-center" role="alert">
					<AlertTriangle className="h-10 w-10 text-error" aria-hidden="true" />
					<p className="text-lg font-semibold text-primary">{t("error.loadFailed")}</p>
					<p className="text-sm text-secondary">{t("error.loadFailedDesc")}</p>
				</div>
			</div>
		);
	}

	if (!content || !currentModule) {
		return (
			<div className="flex h-[calc(100dvh-48px)] items-center justify-center">
				<p className="text-secondary">{t("course.notFound")}</p>
			</div>
		);
	}

	return (
		<div className="flex h-[calc(100dvh-48px)] flex-col lg:flex-row">
			<QuizCelebration active={showCelebration} />

			{/* Module sidebar - desktop */}
			<aside
				className={cn(
					"hidden flex-col border-r border-border-subtle bg-bg-surface transition-all duration-300 ease-out motion-reduce:transition-none overflow-hidden lg:flex",
					sidebarOpen ? "w-72" : "w-0",
				)}
			>
				<div className="flex items-center justify-between border-b border-border-subtle px-4 py-3">
					<div className="min-w-0">
						<p className="truncate text-sm font-medium text-primary">{content.course.title}</p>
						<div className="mt-1 flex items-center gap-2" aria-live="polite">
							<ProgressRing progress={overallProgress} size="sm" />
							<span className="text-xs text-muted">{overallProgress}%</span>
						</div>
					</div>
				</div>
				<div className="flex-1 overflow-y-auto">
					{sections.map((section) => {
						const isCollapsed = collapsedSections.has(section.id);
						const completedInSection = section.modules.filter(
							(m) => m.progress?.status === "completed",
						).length;
						return (
							<div key={section.id}>
								<button
									type="button"
									onClick={() => toggleSidebarSection(section.id)}
									className="flex w-full items-center justify-between px-4 pt-4 pb-1 text-left"
								>
									<span className="text-xs font-semibold uppercase tracking-wider text-muted">
										{section.title}
									</span>
									<div className="flex items-center gap-1.5">
										<span className="text-xs text-muted">
											{completedInSection}/{section.modules.length}
										</span>
										<ChevronDown
											className={cn(
												"h-3.5 w-3.5 text-muted transition-transform duration-300 ease-out motion-reduce:transition-none",
												isCollapsed && "-rotate-90",
											)}
											aria-hidden="true"
										/>
									</div>
								</button>
								<div
									className={cn(
										"grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none",
										isCollapsed ? "grid-rows-[0fr]" : "grid-rows-[1fr]",
									)}
								>
									<div className="overflow-hidden">
										{section.modules.map((mod) => (
											<ModuleItem
												key={mod.id}
												module={mod}
												isCurrent={mod.id === currentModule.id}
												onClick={() => navigateToModule(mod)}
											/>
										))}
									</div>
								</div>
							</div>
						);
					})}
				</div>
			</aside>

			{/* Main content */}
			<div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
				{/* Top bar */}
				<div className="flex shrink-0 items-center gap-3 border-b border-border-subtle bg-bg-surface px-4 py-2">
					<button
						type="button"
						onClick={() => setSidebarOpen((o) => !o)}
						className="hidden min-h-[44px] min-w-[44px] items-center justify-center rounded-md text-secondary hover:text-primary lg:flex"
						aria-label={sidebarOpen ? "Close sidebar" : "Open sidebar"}
					>
						{sidebarOpen ? (
							<PanelLeftClose className="h-4 w-4" />
						) : (
							<PanelLeftOpen className="h-4 w-4" />
						)}
					</button>
					<span className="min-w-0 flex-1 text-sm font-medium text-primary">
						{currentModule.position}. {currentModule.title}
					</span>
					<LessonDescription
						key={currentModule.id}
						title={currentModule.title}
						description={currentModule.description}
					/>
				</div>

				<div className="min-h-0 flex-1 overflow-y-auto">
					{/* Video player */}
					<VideoPlayer
						key={currentModule.id}
						videoUrl={currentModule.video_url}
						moduleId={currentModule.id}
						initialPosition={currentModule.progress?.video_position ?? 0}
						onComplete={handleVideoComplete}
					/>

					{/* Mobile module list - horizontal scroll */}
					<div className="border-b border-border-subtle overflow-x-auto lg:hidden">
						<div className="flex gap-1 p-2">
							{modules.map((mod) => (
								<button
									key={mod.id}
									type="button"
									onClick={() => navigateToModule(mod)}
									className={cn(
										"flex-shrink-0 rounded-md px-3 py-2 text-xs font-medium whitespace-nowrap transition-colors min-h-[40px]",
										mod.id === currentModule.id
											? "bg-accent text-white"
											: mod.progress?.status === "completed"
												? "bg-success-muted text-success"
												: "bg-bg-elevated text-secondary hover:text-primary",
									)}
								>
									{mod.position}. {mod.title}
								</button>
							))}
						</div>
					</div>

					{/* Quiz */}
					{currentModule.has_quiz && quiz ? (
						<div className="space-y-6 p-4 sm:p-6">
							<h3 className="text-lg font-semibold text-primary">
								{t("quiz.title", { title: quiz.module_title })}
							</h3>

							{quizResult ? (
								<div aria-live="polite">
									<QuizResult
										result={quizResult}
										onRetry={quizResult.passed ? undefined : handleQuizRetry}
									/>
								</div>
							) : (
								<>
									{quiz.questions.map((question, i) => (
										<QuizCard
											key={question.id}
											question={question}
											questionIndex={i}
											totalQuestions={quiz.questions.length}
											selectedAnswers={quizAnswers[question.id] ?? []}
											onSelect={(optionId) => handleQuizSelect(question.id, optionId)}
										/>
									))}
									<Button
										onClick={handleQuizSubmit}
										disabled={submit.isPending}
										className="mt-4 min-h-[44px]"
									>
										{submit.isPending && (
											<Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
										)}
										{t("quiz.submit")}
									</Button>
								</>
							)}
						</div>
					) : null}
				</div>
			</div>
		</div>
	);
}
